<script setup lang="ts">

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import ItemV3MostSelected from "@/components/itemV3/parameter/itemV3MostSelected.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const {t} = useI18n();

const emit = defineEmits(["patch"]);
const props = defineProps(["item"]);

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const imageWidthPercent: number = 30
const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const boxIdx = "finish"

const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const variantIdx = "discs.*.finish"

const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const currentDiscsFinishText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const open = ref(false)

const discVariants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeDiscIdx = ref(0)

const discSliderStyle = computed(() => {
  const count = discVariants.value.length;
  if (count === 0 || activeDiscIdx.value < 0 || activeDiscIdx.value >= count) return {display: 'none'};

  const width = 100 / count;
  const left = activeDiscIdx.value * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
});

const finishVariantsOfDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value].values
})

const activeDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value]
})

const matchedDiscIdx = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return -1
  return discVariants.value[activeDiscIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * image
 */

const hoverFinishIdx = ref(-1)

const setHoverFinishIdx = () => {
  if (matchedDiscIdx.value !== -1 && finishVariantsOfDisc.value.length >= matchedDiscIdx.value) {
    hoverFinishIdx.value = matchedDiscIdx.value
  }
}

watch(activeDisc, () => {
  setHoverFinishIdx()
})

watch(props.item, () => {
  setHoverFinishIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(finishVariantsOfDisc.value, hoverFinishIdx.value))

const getFinishImageUrl = (finish: string, color: string, sizeX: number, sizeY: number) => {
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/finishes/' + sizeX + 'x' + sizeY + '/' + finish.toLowerCase() + '_' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const discs = discVariants.value;
  if (!discs.length) return;

  const allListsCreated = discs.every((disc: any) => disc.values && disc.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = discs.flatMap((disc: any) => disc.values.map((color: any) => getFinishImageUrl(color.idx, color.additional_value, 400, 300)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(discVariants, () => {
  checkAllLoaded();
  if (discVariants.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/*
 * focus values
 */

const focusValueIdx = [
  "matt",
  "polished",
  "ice_matt",
  "hammered_matt",
  "sand_matt"
]

const getFocusValueIdx = computed(
    () => {
      if (matchedDiscIdx.value) {
        if (!focusValueIdx.includes(discVariants.value[activeDiscIdx.value].values[matchedDiscIdx.value].idx)) {
          return [
            discVariants.value[activeDiscIdx.value].values[matchedDiscIdx.value].idx,
            ...focusValueIdx.slice(0, -1)
          ]
        }
      }

      return focusValueIdx

    }
)

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getFinishTipTitle = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    const key = activeDisc.value.values[hoverFinishIdx.value].idx.toLowerCase()
    return t('components.parameters.tips.finishes.' + key + '.title')
  } else {
    return ''
  }
})

const getFinishTipText = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    const key = activeDisc.value.values[hoverFinishIdx.value].idx.toLowerCase()
    return t('components.parameters.tips.finishes.' + key + '.text')
  } else {
    return ''
  }
})

const getFinishTipImageUrl = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverFinishIdx.value]) {
    return getFinishImageUrl(activeDisc.value.values[hoverFinishIdx.value].idx, activeDisc.value.values[hoverFinishIdx.value].additional_value, 800, 600)
  } else {
    return ''
  }
})

onMounted(
    () => {
      setHoverFinishIdx()
    }
)

</script>

<template>
  <item-v3-parameter-box
      v-model:open="open"
      :image-width-percent=imageWidthPercent
      :image=imageUrl
      :header-image="getHeaderImage()"
      :header-image-srcset="getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w'"
      title="Oberfläche"
      :subtitle="currentDiscsFinishText || 'Wählen Sie die Oberfläche für Ihre Trauringe'"
      :loading=loading
      :tip-title="getFinishTipTitle"
      :tip-text="getFinishTipText"
      :tip-image="getFinishTipImageUrl"
  >
    <template v-slot:main
    >
      <nav v-if="discVariants.length > 1" class="w-full mb-4">
        <!-- disc selection -->
        <div
            class="relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none">
          <!-- Sliding Background -->
          <div
              class="absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none"
              :style="discSliderStyle"
          ></div>

          <!-- Disc Buttons -->
          <button
              v-for="(disc, discIdx) in discVariants"
              :key="discIdx"
              type="button"
              class=" text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full"
              :class="activeDiscIdx === discIdx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700'"
              @click="activeDiscIdx = discIdx"
          >
            Scheibe {{ discIdx + 1 }}
          </button>
        </div>
      </nav>
      <!-- most selected -->
      <item-v3-most-selected/>
      <ul class="flex flex-wrap items-start w-full justify-center ">
        <!-- disc list -->
        <template v-for="(finish, finishIdx) in finishVariantsOfDisc">
          <li :key="finishIdx"
              v-if="getFocusValueIdx.includes(finish.idx.toString())"
              class="cursor-pointer flex flex-col py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] items-center justify-start"
              @click="handleValuePatch(activeDisc.idx, finish)"
              @mouseover="hoverFinishIdx = finishIdx as number"
              @mouseleave="hoverFinishIdx = matchedDiscIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': finish.match,
              'border-2 border-transparent': !finish.match
            }"
          >
            <img
                :src="getFinishImageUrl(finish.idx, finish.additional_value, 400, 300)"
                :srcset="getFinishImageUrl(finish.idx, finish.additional_value,800, 600)"
                class="w-12 md:w-13 aspect-4:3 rounded-2xl"
            >
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-14 mx-auto text-center mt-1">
            {{ finish.title }}
          </span>
          </li>
        </template>
      </ul>

    </template>
    <template v-slot:more>
      <ul class="flex flex-wrap items-start w-full justify-center ">
        <!-- disc list -->
        <template v-for="(finish, finishIdx) in finishVariantsOfDisc">
          <li :key="finishIdx"
              v-if="!getFocusValueIdx.includes(finish.idx.toString())"
              class="cursor-pointer flex flex-col py-2 px-1 rounded-2xl hover:bg-[#f3f0e9] items-center justify-start"
              @click="handleValuePatch(activeDisc.idx, finish)"
              @mouseover="hoverFinishIdx = finishIdx as number"
              @mouseleave="hoverFinishIdx = matchedDiscIdx"
              :class="{
              'border-2 border-brand-main bg-[#f3f0e9]': finish.match,
              'border-2 border-transparent': !finish.match
            }"
          >
            <img
                :src="getFinishImageUrl(finish.idx, finish.additional_value, 400, 300)"
                :srcset="getFinishImageUrl(finish.idx, finish.additional_value,800, 600)"
                class="w-12 md:w-13 aspect-4:3 rounded-2xl"
            >
            <span class="text-parameter-text text-[10px] overflow-hidden max-w-13 mx-auto text-center mt-1">
            {{ finish.title }}
          </span>
          </li>
        </template>
      </ul>
    </template>
  </item-v3-parameter-box>
</template>

<style scoped>

</style>